import React, { useState } from 'react';
import { Buildings, CaretDown, CaretRight, Copy, Check } from '@phosphor-icons/react';

/* -------------------------------------------------------------
   Mockup van het 'Bedrijf - Algemeen' instellingen-scherm.
   Geïnspireerd op de bijlage van de gebruiker.
   - Horizontale tabs bovenaan (Bedrijf, Bank, Bijlagen, ...)
   - Elke sectie inklapbaar (Bedrijfsgegevens, Adressen, Bedrijfsinstellingen)
   - Compact, ruime invoervelden
   - Niet-functioneel — puur visueel voor goedkeuring
   Route: /bedrijf-mockup
   ------------------------------------------------------------- */

const TABS = ['Bedrijf', 'Bank', 'Bijlagen', 'Informatie', 'Historiek', 'Bedrijfsdocumenten', 'E-Invoicing', 'Partner'];

export default function BedrijfMockupPage() {
  const [tab, setTab] = useState('Bedrijf');
  const [open, setOpen] = useState({
    bedrijf: true,
    contact: true,
    fact: true,
    aflev: false,
    instellingen: false,
  });
  const toggle = (k) => setOpen(p => ({ ...p, [k]: !p[k] }));

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="bedrijf-mockup-page">
      {/* Banner met uitleg */}
      <div className="bg-amber-50 border border-amber-300 rounded p-3 mb-4 text-xs text-amber-900 flex items-start gap-2">
        <span>👀</span>
        <div>
          <strong>Voorbeeld-modus</strong> — dit is een mockup om de nieuwe layout te beoordelen.
          Geen velden zijn echt opgeslagen. Bevalt dit? Dan rollen we dezelfde stijl uit op de échte Instellingen / Bedrijf-pagina.
        </div>
      </div>

      {/* Bedrijfskop */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
          <Buildings size={22} className="text-[#002FA7]" weight="bold" />
          <h1 className="text-xl font-bold text-gray-900">Alphaprofiles</h1>
        </div>

        {/* Horizontale tabs */}
        <div className="px-5 border-b border-gray-100">
          <div className="flex items-center gap-1 overflow-x-auto">
            {TABS.map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-3 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  tab === t ? 'text-[#002FA7] border-[#002FA7]' : 'text-gray-500 border-transparent hover:text-gray-800'
                }`}
                data-testid={`tab-${t.toLowerCase()}`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* Inhoud */}
        <div className="p-5 space-y-4">
          {/* ===== Sectie 1: Bedrijfsgegevens ===== */}
          <Section title="Bedrijfsgegevens" open={open.bedrijf} onToggle={() => toggle('bedrijf')} testid="sec-bedrijf">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
              <div>
                <Field label="Ondernemingsnummer" required>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900">BE0803088437</span>
                    <button className="px-2 py-0.5 text-xs border border-gray-300 rounded hover:bg-gray-50">+</button>
                  </div>
                </Field>
                <div className="flex items-center gap-4 mt-2 ml-1">
                  <label className="flex items-center gap-1.5 text-xs">
                    <input type="checkbox" defaultChecked className="accent-orange-500" /> Btw-plichtig
                  </label>
                  <label className="flex items-center gap-1.5 text-xs">
                    <input type="checkbox" /> Kleine onderneming
                  </label>
                </div>
                <Field label="Maatschappelijke naam" required><Input value="Alphaprofiles" /></Field>
                <Field label="Commerciële naam"><Input value="Alphaprofiles" /></Field>
                <Field label="IBAN"><Input value="BE79001953350533" /></Field>
                <Field label="BIC"><Input value="GEBABEBB" /></Field>
                <Field label="Domiciliëringsidentificatie"><Input value="" /></Field>
                <Field label="Standaard munteenheid"><Select options={['EUR', 'USD']} /></Field>
                <Field label="Btw"><Select options={['21%', '12%', '6%', '0%']} /></Field>
                <Field label="Grootboekrekening"><Input value="" /></Field>
              </div>
              <div>
                <Field label="Aanspreking"><Input value="" /></Field>
                <Field label="Voornaam bestuurder"><Input value="Ahmet" /></Field>
                <Field label="Achternaam bestuurder"><Input value="Gonca" /></Field>
                <Field label="Rechtspersonenregister (RPR)"><Input value="" /></Field>
                <Field label="E-mail" required><Input value="info@alphaprofiles.com" /></Field>
                <Field label="Website"><Input value="www.alphaprofiles.com" /></Field>
                <Field label="Telefoon"><PhoneInput value="+32 475 22 32 11" /></Field>
                <Field label="Mobiel"><PhoneInput value="+32 475 22 32 11" /></Field>
                <Field label="Fax"><PhoneInput value="" /></Field>
                <Field label="Taal"><Select options={['Nederlands (NL)', 'Français (FR)', 'English (EN)']} /></Field>
              </div>
            </div>
          </Section>

          {/* ===== Sectie 2: Facturatieadres ===== */}
          <Section
            title="Facturatieadres"
            open={open.fact}
            onToggle={() => toggle('fact')}
            testid="sec-fact"
            right={<button className="text-xs text-orange-600 hover:text-orange-800 flex items-center gap-1"><Copy size={12} /> Kopieer</button>}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
              <div>
                <Field label="Tav"><Textarea value="" /></Field>
                <Field label="Commerciële naam"><Input value="Alphaprofiles BV" /></Field>
                <Field label="Land"><Select options={['België', 'Nederland', 'Frankrijk']} /></Field>
                <Field label="Straat"><Input value="Elektronikalaan" /></Field>
                <Field label="Nr"><Input value="1" /></Field>
                <Field label="Bus"><Input value="B" /></Field>
                <Field label="Postcode"><Input value="2610" /></Field>
                <Field label="Gemeente"><Input value="Antwerpen" /></Field>
                <Field label="Telefoon"><PhoneInput value="+32 475 22 32 11" /></Field>
              </div>
            </div>
          </Section>

          {/* ===== Sectie 3: Afleveradres ===== */}
          <Section
            title="Afleveradres"
            open={open.aflev}
            onToggle={() => toggle('aflev')}
            testid="sec-aflev"
            right={
              <div className="flex items-center gap-3">
                <button className="text-xs text-orange-600 hover:text-orange-800 flex items-center gap-1"><Copy size={12} /> Kopieer</button>
                <label className="flex items-center gap-2 text-xs">
                  <span>Zelfde als facturatieadres</span>
                  <span className="inline-block w-8 h-4 bg-orange-500 rounded-full relative">
                    <span className="absolute right-0.5 top-0.5 w-3 h-3 bg-white rounded-full"></span>
                  </span>
                </label>
              </div>
            }
          >
            <div className="text-xs text-gray-500 italic">Wordt automatisch overgenomen van facturatieadres.</div>
          </Section>

          {/* ===== Sectie 4: Bedrijfsinstellingen ===== */}
          <Section title="Bedrijfsinstellingen" open={open.instellingen} onToggle={() => toggle('instellingen')} testid="sec-bedrijfsinst" titleStyle="bold">
            <div className="space-y-3">
              <Field label="Logo (aangeraden afmetingen zijn breedte: 400px en hoogte: 200px)">
                <div className="flex items-center gap-3">
                  <input type="file" className="text-xs" />
                  <span className="text-xs text-gray-500 italic">Bewaar hier je logo en algemene voorwaarden. Ze worden automatisch toegevoegd aan je offertes, facturen en creditnota's.</span>
                </div>
              </Field>
              <div className="border border-gray-200 rounded p-4 bg-gray-50 text-center">
                <div className="text-2xl font-black tracking-wider text-gray-700">/ALPHA</div>
                <div className="text-xs font-semibold tracking-[0.3em] text-gray-500 mt-0.5">P R O F I L E S</div>
              </div>
              <div className="text-right">
                <button className="text-xs text-orange-600 hover:text-orange-800">Wissen</button>
              </div>
            </div>
          </Section>
        </div>
      </div>

      {/* Floating actie-balk */}
      <div className="mt-4 flex items-center justify-between gap-3">
        <div className="text-xs text-gray-500 flex items-center gap-1.5">
          <Check size={14} className="text-green-600" /> Gesynchroniseerd
        </div>
        <div className="flex items-center gap-2">
          <button className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900">Annuleer</button>
          <button className="px-5 py-2 text-sm font-bold text-white bg-[#002FA7] rounded hover:bg-[#001F87]">Opslaan</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Hulp-componenten ─── */

function Section({ title, open, onToggle, children, right = null, testid = '', titleStyle = 'medium' }) {
  return (
    <div className={`border ${open ? 'border-gray-200' : 'border-gray-100'} rounded-lg overflow-hidden`} data-testid={testid}>
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors" onClick={onToggle}>
        <div className="flex items-center gap-2">
          {open ? <CaretDown size={14} className="text-gray-500" weight="bold" /> : <CaretRight size={14} className="text-gray-500" weight="bold" />}
          <h3 className={`text-sm uppercase tracking-wide text-gray-700 ${titleStyle === 'bold' ? 'font-extrabold' : 'font-bold'}`}>{title}</h3>
        </div>
        {right && <div onClick={(e) => e.stopPropagation()}>{right}</div>}
      </div>
      {open && <div className="p-4 bg-white">{children}</div>}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="grid grid-cols-12 gap-4 items-center py-1.5">
      <label className="col-span-5 text-xs text-gray-600 text-right">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="col-span-7">{children}</div>
    </div>
  );
}

function Input({ value }) {
  return <input type="text" defaultValue={value} className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:border-[#002FA7] focus:outline-none focus:ring-1 focus:ring-[#002FA7]" />;
}

function Textarea({ value }) {
  return <textarea defaultValue={value} rows={2} className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:border-[#002FA7] focus:outline-none focus:ring-1 focus:ring-[#002FA7]" />;
}

function Select({ options }) {
  return (
    <select className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:border-[#002FA7] focus:outline-none focus:ring-1 focus:ring-[#002FA7]">
      {options.map(o => <option key={o}>{o}</option>)}
    </select>
  );
}

function PhoneInput({ value }) {
  return (
    <div className="flex">
      <span className="px-2 py-1.5 text-xs font-semibold border border-r-0 border-gray-300 bg-gray-50 rounded-l">BE ▾</span>
      <input type="text" defaultValue={value} className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-r focus:border-[#002FA7] focus:outline-none focus:ring-1 focus:ring-[#002FA7]" />
    </div>
  );
}
